import { useAccount } from "@/context/account";
import {
  Autocomplete,
  Button,
  DropdownMenu,
  Icon,
  MenuItem,
} from "@trimble/modus-cmx";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import "./GlobalSearch.scss";
import { ContractOption } from "@/components/Navigation/GlobalHeader/GlobalSearch/components/ContractOption/ContractOption";
import { useGlobalSearchSuggestions } from "./useGlobalSearchSuggestions";
import { DeviceOption } from "./components/DevicesOption/DeviceOption";
import type {
  ContractAutocompleteOptions,
  CustomerSearchItems,
  DealerAutocompleteOptions,
  DevicesAutocompleteOptions,
  InvoiceAutocompleteOptions,
  SearchScope,
} from "./types";
import { SEARCH_CATEGORY, isSearchScope } from "./types";
import { DealerOption } from "./components/DealerOption/DealerOption";
import { InvoiceOption } from "./components/InvoiceOption/InvoiceOption";
import { CustomerOption } from "./components/CustomerOption/CustomerOption";

function getAutocompleteInputValue(detail: unknown): string {
  if (
    detail &&
    typeof detail === "object" &&
    "target" in detail &&
    detail.target instanceof HTMLInputElement
  ) {
    return detail.target.value;
  }
  return "";
}

export function GlobalSearch() {
  const { t } = useTranslation();
  const { accountRouteId } = useAccount();
  const navigate = useNavigate();

  const [searchCategory, setSearchCategory] =
    useState<SearchScope>("contracts");
  const [expanded, setExpanded] = useState(false);
  const searchCategoryRef = useRef(searchCategory);
  searchCategoryRef.current = searchCategory;

  const [searchText, setSearchText] = useState("");
  const trimmedSearch = searchText.trim();
  const {
    options,
    setOptions,
    loading,
    setLoading,
    debouncedFetch,
    resetSuggestions,
  } = useGlobalSearchSuggestions(searchCategoryRef);

  const dropdownRef = useRef<HTMLModusWcDropdownMenuElement | null>(null);
  const modifierKeyRef = useRef(false);
  const [autocompleteKey, setAutocompleteKey] = useState(0);

  useEffect(() => {
    const track = (e: KeyboardEvent | MouseEvent) => {
      modifierKeyRef.current = e.ctrlKey || e.metaKey;
    };
    document.addEventListener("keydown", track);
    document.addEventListener("keyup", track);
    document.addEventListener("mousedown", track);
    return () => {
      document.removeEventListener("keydown", track);
      document.removeEventListener("keyup", track);
      document.removeEventListener("mousedown", track);
    };
  }, []);

  function navigateTo(path: string) {
    if (modifierKeyRef.current) {
      window.open(path, "_blank", "noopener,noreferrer");
    } else {
      void navigate(path);
    }
  }

  const closeDropdown = () => {
    if (dropdownRef.current) {
      dropdownRef.current.menuVisible = false;
    }
  };

  function clearSearchInput() {
    setSearchText("");
    setOptions({ totalCount: 0, items: [] });
    setAutocompleteKey((prev) => prev + 1);
  }

  function handleViewAllContracts() {
    if (accountRouteId) {
      navigateTo(
        `/${accountRouteId}/contracts?search=${encodeURIComponent(trimmedSearch)}`
      );
    }
    clearSearchInput();
  }

  function onHandleInputChange(value: string) {
    setSearchText(value);
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      resetSuggestions();
      return;
    }
    setLoading(true);
    debouncedFetch(trimmed);
  }

  useEffect(() => {
    resetSuggestions();
    setSearchText("");
    setAutocompleteKey((prev) => prev + 1);
  }, [searchCategory, resetSuggestions]);

  const navigationPathByCategory: Record<
    SearchScope,
    ((id: string) => string) | null
  > = {
    contracts: (id) => `/${accountRouteId}/contracts/${id}`,
    devices: () =>
      `/${accountRouteId}/devices?search=${encodeURIComponent(trimmedSearch)}`,
    customers: (id) => `/${accountRouteId}/customers/${id}`,
    dealers: () =>
      `/${accountRouteId}/dealer-members?search=${encodeURIComponent(trimmedSearch)}`,
    invoices: () =>
      `/${accountRouteId}/invoices?search=${encodeURIComponent(trimmedSearch)}`,
  };

  function handleViewAllCustomers() {
    if (accountRouteId) {
      navigateTo(
        `/${accountRouteId}/customers?search=${encodeURIComponent(trimmedSearch)}`
      );
    }
    clearSearchInput();
  }

  function handleNavigation(id: string) {
    if (id === "view-all") return;
    if (!accountRouteId) {
      clearSearchInput();
      return;
    }
    const buildPath = navigationPathByCategory[searchCategory];
    if (buildPath) navigateTo(buildPath(id));
    clearSearchInput();
  }

  const searchOptionsByCategory: Record<SearchScope, React.ReactNode> = {
    contracts: (
      <ContractOption
        options={options.items as ContractAutocompleteOptions[]}
        totalCount={options.totalCount}
        searchTerm={trimmedSearch}
        onViewAll={handleViewAllContracts}
      />
    ),
    devices: (
      <DeviceOption
        options={options.items as DevicesAutocompleteOptions[]}
        totalCount={options.totalCount}
        searchTerm={trimmedSearch}
        onViewAll={() => {
          handleNavigation(trimmedSearch);
        }}
      />
    ),
    customers: (() => {
      const customerItems: CustomerSearchItems = Array.isArray(options.items)
        ? { companyOptions: [], roleOptions: [] }
        : options.items;
      return (
        <CustomerOption
          companyOptions={customerItems.companyOptions}
          roleOptions={customerItems.roleOptions}
          totalCount={options.totalCount}
          searchTerm={trimmedSearch}
          onViewAll={handleViewAllCustomers}
        />
      );
    })(),
    dealers: (
      <DealerOption
        options={options.items as DealerAutocompleteOptions[]}
        searchTerm={trimmedSearch}
        onViewAll={() => {
          handleNavigation(trimmedSearch);
        }}
      />
    ),
    invoices: (
      <InvoiceOption
        options={options.items as InvoiceAutocompleteOptions[]}
        totalCount={options.totalCount}
        searchTerm={trimmedSearch}
        onViewAll={() => {
          handleNavigation(trimmedSearch);
        }}
      />
    ),
  };

  const showNoSearchResults =
    trimmedSearch.length > 0 && !loading && options.totalCount === 0;

  return !expanded ? (
    <Button
      id="global-search-btn"
      onButtonClick={() => {
        setExpanded(true);
      }}
      variant="borderless"
      shape="circle"
      color="neutral"
      aria-label={t("global_search.search")}
    >
      <Icon name="search" size="md" decorative />
    </Button>
  ) : (
    <div className="global-search-container">
      <DropdownMenu
        ref={dropdownRef}
        id="global-search-menu"
        buttonAriaLabel={t("global_search.dropdown_menu_button_aria_label")}
        buttonColor="tertiary"
        buttonSize="sm"
        menuOffset={14}
      >
        <div slot="button" className="global-search-dropdown-button">
          <span>{t(`global_search.${searchCategory}`)}</span>
          <Icon name="expand_more" variant="solid" size="md" />
        </div>
        <div slot="menu">
          {SEARCH_CATEGORY.map((value) => (
            <MenuItem
              key={value}
              label={t(`global_search.${value}`)}
              value={value}
              selected={searchCategory === value}
              onItemSelect={(e) => {
                resetSuggestions();
                const next = e.detail.value;
                if (isSearchScope(next)) {
                  setSearchCategory(next);
                  closeDropdown();
                }
              }}
            />
          ))}
        </div>
      </DropdownMenu>
      <div className="global-search-autocomplete-wrapper">
        <Autocomplete
          key={autocompleteKey}
          placeholder={t(
            `global_search.search_${searchCategory.toLowerCase()}_placeholder`
          )}
          size="sm"
          includeClear
          includeSearch
          showMenuOnFocus
          leaveMenuOpen
          showSpinner={loading}
          onInputChange={(e) => {
            const value = getAutocompleteInputValue(e.detail);
            onHandleInputChange(value);
          }}
          onItemSelect={(e) => {
            handleNavigation(e.detail.value);
          }}
        >
          <div slot="menu-items">
            {showNoSearchResults ? (
              <div
                className="global-search-no-results"
                role="status"
                aria-live="polite"
              >
                <span className="global-search-no-results-title">
                  <Icon
                    name="search"
                    variant="solid"
                    size="md"
                    className="global-search-no-results-icon"
                  />
                  {t("global_search.no_search_results")}
                </span>
                <span className="global-search-no-results-hint">
                  {t("global_search.no_search_results_hint")}
                </span>
              </div>
            ) : (
              searchOptionsByCategory[searchCategory]
            )}
          </div>
        </Autocomplete>
      </div>
    </div>
  );
}
